import { Card, CardBody, Avatar } from '@heroui/react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { BiChevronLeft, BiChevronRight, BiX } from 'react-icons/bi';
import { withCdnUrl } from '../../../../utils/imageUtils';
import CommonReply from './CommonReply';
import StarRating from '../../StarRating';
import { BriefcaseBusiness } from 'lucide-react';

const DEFAULT_FALLBACK_IMAGE =
  'https://cdn.hirepanther.ca/common/default-fallback-image.png';
const AUTO_SLIDE_INTERVAL_MS = 4500;
const COMMENT_CHAR_LIMIT = 220;
const MAX_VISIBLE_THUMBS = 5;

interface ReusableCardProps {
  className?: string;
  data: any;
}

// Rating drives a small semantic accent — not decoration, it tells the
// reader at a glance whether this is a happy, mixed, or unhappy review.
const getRatingAccent = (rating: number) => {
  if (rating >= 4) {
    return {
      bar: 'bg-emerald-500',
      chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    };
  }
  if (rating >= 3) {
    return {
      bar: 'bg-amber-500',
      chip: 'bg-amber-50 text-amber-700 ring-amber-200',
    };
  }
  return {
    bar: 'bg-rose-500',
    chip: 'bg-rose-50 text-rose-700 ring-rose-200',
  };
};

const ReviewsMessage = ({ data, className = '' }: ReusableCardProps) => {
  const hasRealImages = !!(data?.imageUrls && data.imageUrls.length > 0);
  const images: any[] = hasRealImages ? data.imageUrls : [];
  const hasMultipleImages = images.length > 1;
  const rating = Number(data?.rating) || 0;
  const accent = getRatingAccent(rating);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const comment: string = data?.comment || '';
  const isLongComment = comment.length > COMMENT_CHAR_LIMIT;
  const displayComment =
    isExpanded || !isLongComment
      ? comment
      : `${comment.slice(0, COMMENT_CHAR_LIMIT).trimEnd()}…`;

  const getImageSrc = (img: any) => {
    if (!img?.imageName) return DEFAULT_FALLBACK_IMAGE;
    return withCdnUrl(img.imageName);
  };

  // Auto-advance the preview image only — no peeking side panels to manage.
  useEffect(() => {
    if (!hasMultipleImages || isPaused || showImageModal) return;
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % images.length);
    }, AUTO_SLIDE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasMultipleImages, isPaused, showImageModal, images.length]);

  // Let Escape close the lightbox, and stop the page scrolling behind it.
  useEffect(() => {
    if (!showImageModal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowImageModal(false);
      if (e.key === 'ArrowRight') nextImageSlide();
      if (e.key === 'ArrowLeft') prevImageSlide();
    };
    document.addEventListener('keydown', onKeyDown);
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = original;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showImageModal, images.length]);

  const goPrev = () =>
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  const goNext = () => setActiveIndex((prev) => (prev + 1) % images.length);

  const handleOpenImageModal = (index: number) => {
    if (!hasRealImages) return;
    setModalIndex(index);
    setShowImageModal(true);
  };

  const nextImageSlide = () =>
    setModalIndex((prev) => (prev + 1) % images.length);
  const prevImageSlide = () =>
    setModalIndex((prev) => (prev - 1 + images.length) % images.length);

  const visibleThumbs = images.slice(0, MAX_VISIBLE_THUMBS);
  const remainingCount = images.length - MAX_VISIBLE_THUMBS;

  return (
    <div>
      <Card
        className={`relative overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 h-full ${className}`}
      >
        {/* Sentiment accent bar */}
        <div className={`absolute left-0 top-0 h-full w-1 ${accent.bar}`} />

        <CardBody className="pl-5 pr-4 py-4">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            {/* Left: reviewer + rating + optional image */}
            <div className="md:w-64 flex-shrink-0 flex flex-col gap-3">
              <div className="flex items-center gap-2.5">
                <Avatar
                  name={
                    data?.customerName?.trim().substring(0, 2).toUpperCase() ??
                    'NO'
                  }
                  alt={data?.customerName || 'Anonymous User'}
                  size="sm"
                  color="secondary"
                />
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-gray-900 truncate">
                    {data?.customerName || 'Anonymous User'}
                  </div>
                  {data?.reviewerInfo?.userType && (
                    <div className="text-[11px] font-medium text-primary">
                      {data.reviewerInfo.userType}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <div className="flex gap-0.5">
                  <StarRating
                    value={data.overallRating || 0}
                    showValue={true}
                    size="sm"
                  />
                </div>
              </div>

              <p
                className="text-[11px] text-gray-400"
                title={
                  data?.createdAt
                    ? moment(data.createdAt).format('LLL')
                    : undefined
                }
              >
                {data?.createdAt ? moment(data.createdAt).fromNow() : '-'}
              </p>

              {hasRealImages && (
                <div
                  className="relative w-full"
                  onMouseEnter={() => setIsPaused(true)}
                  onMouseLeave={() => setIsPaused(false)}
                >
                  <div
                    className="relative w-full aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer group"
                    onClick={() => handleOpenImageModal(activeIndex)}
                  >
                    {images.map((img, i) => (
                      <img
                        key={i}
                        src={getImageSrc(img)}
                        alt={`review-${i}`}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                          i === activeIndex ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                    ))}

                    {hasMultipleImages && (
                      <div className="absolute inset-0 flex items-center justify-between px-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            goPrev();
                          }}
                          className="w-6 h-6 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"
                          aria-label="Previous image"
                        >
                          <BiChevronLeft size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            goNext();
                          }}
                          className="w-6 h-6 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"
                          aria-label="Next image"
                        >
                          <BiChevronRight size={16} />
                        </button>
                      </div>
                    )}

                    {images.length > 1 && (
                      <span className="absolute bottom-1.5 right-1.5 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                        {activeIndex + 1}/{images.length}
                      </span>
                    )}
                  </div>

                  {hasMultipleImages && (
                    <div className="flex gap-1.5 mt-1.5">
                      {visibleThumbs.map((img, i) => (
                        <button
                          key={i}
                          onClick={() => setActiveIndex(i)}
                          className={`relative w-9 h-9 rounded-md overflow-hidden flex-shrink-0 ring-2 transition-all ${
                            i === activeIndex
                              ? 'ring-primary'
                              : 'ring-transparent opacity-70 hover:opacity-100'
                          }`}
                          aria-label={`View image ${i + 1}`}
                        >
                          <img
                            src={getImageSrc(img)}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                          {i === MAX_VISIBLE_THUMBS - 1 &&
                            remainingCount > 0 && (
                              <span className="absolute inset-0 bg-black/50 text-white text-[10px] font-semibold flex items-center justify-center">
                                +{remainingCount}
                              </span>
                            )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right: title, comment, provider reply */}
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              {data.title && (
                <div className="flex items-center gap-2">
                  <BriefcaseBusiness className="w-4 h-4 text-primary flex-shrink-0" />
                  <p className="text-sm font-semibold text-gray-900">
                    {data.title}
                  </p>
                </div>
              )}

              <p className="text-sm leading-relaxed text-gray-600 whitespace-pre-line">
                {displayComment || '—'}
              </p>

              {isLongComment && (
                <button
                  onClick={() => setIsExpanded((prev) => !prev)}
                  className="self-start text-xs font-medium text-primary hover:underline"
                >
                  {isExpanded ? 'Show less' : 'Read more'}
                </button>
              )}

              {data?.providerResponse && (
                <div className="mt-2 pl-3 border-l-2 border-gray-200">
                  <CommonReply
                    title="Provider Reply"
                    description={data?.providerResponse?.responseText || 'N/A'}
                  />
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Image lightbox */}
      {showImageModal && hasRealImages && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
          onClick={() => setShowImageModal(false)}
        >
          <button
            onClick={() => setShowImageModal(false)}
            className="absolute top-5 right-5 z-20 w-11 h-11 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
            aria-label="Close"
          >
            <BiX size={26} />
          </button>

          <div
            className="relative w-full h-full max-w-5xl max-h-[85vh] flex items-center justify-center px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full h-full overflow-hidden rounded-lg flex items-center justify-center">
              <img
                src={getImageSrc(images[modalIndex])}
                alt={`Review ${modalIndex + 1}`}
                className="max-w-full max-h-full object-contain rounded-lg select-none"
              />

              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImageSlide}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                    aria-label="Previous image"
                  >
                    <BiChevronLeft className="w-7 h-7" />
                  </button>
                  <button
                    onClick={nextImageSlide}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                    aria-label="Next image"
                  >
                    <BiChevronRight className="w-7 h-7" />
                  </button>
                </>
              )}
            </div>

            {images.length > 1 && (
              <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 flex gap-2 max-w-full overflow-x-auto px-4">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setModalIndex(index)}
                    className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden transition-all ${
                      index === modalIndex
                        ? 'ring-2 ring-white scale-105'
                        : 'opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={getImageSrc(img)}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            <div className="absolute top-5 left-5 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
              {modalIndex + 1} / {images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsMessage;
